import { Promotion } from '@aboutyou/backbone';
import { Variant } from '@aboutyou/backbone/types/BapiProduct';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { GTMCart } from 'modules/gtm/modules/sections/cart';
import { evaluateCondition } from 'modules/promotions';
import {
  showNotificationAddedToCart,
  showNotificationLimited,
  showNotificationPreRemove,
  showNotificationRemove,
  showNotificationSizeNotSelected,
} from 'modules/shop/notificationManager/cartNotifications';
import { updateSender as scayleUpdateSender } from 'commerce/scayle/checkout/actions';
import {
  getActiveCampaignKey,
  getProductByKey,
} from 'commerce/scayle/product-management';
import {
  addItem,
  getItems,
  removeItem,
  setOrderTotalPrice,
  setSenderId,
  updateItem,
} from 'commerce/scayle/user-interaction';
import {
  BapiProduct,
  BasketResponse,
  CartItem,
} from 'commerce/scayle/user-interaction/types';
import { Product } from 'commerce/scayle/utils/adapter';
import { ClientPageType } from 'src/app/types';
import {
  DAILY_DEAL_PRODUCT_LABEL,
  GIFT_CARD_CATEGORY,
} from 'src/client/components/constants';
import { CartActions } from './types';

interface CartState {
  loading: boolean;
  pageLoading: boolean;
  action: CartActions | null;
  cart: BasketResponse | null;
  promotions: Promotion[] | null;
  campaignKey: string | null;
  preRemovedCartItem: CartItem | null;
  getCart: () => void;
  addToCart: (
    id: string,
    q: number,
    pageType?: ClientPageType,
    limit?: number,
  ) => void;
  updateInCart: (
    id: string | number,
    q: number,
    pageType?: ClientPageType,
  ) => void;
  removeFromCart: (
    key: string,
    name: string,
    pageType?: ClientPageType,
  ) => void;
  clearPreRemovedCartItem: () => void;
  getProductByKey: (key: string | number) => CartItem | undefined;
  getCurrencyCode: () => string;
  setSenderId: (senderId: string) => void;
  updateSender: (senderId: string) => void;
}

const filterInStockProducts = (basket: BasketResponse | undefined) => {
  if (!basket) {
    return undefined;
  }

  const inStockProducts = basket?.items.filter((item) => {
    const isSellableWithoutStock = item.variant.stock.isSellableWithoutStock;
    return item.variant.stock.quantity > 0 || isSellableWithoutStock;
  });

  return {
    ...basket,
    items: inStockProducts,
  };
};

export const useCart = create(
  devtools(
    persist<CartState>(
      (updateCartState, getState) => ({
        action: null,
        cart: null,
        promotions: null,
        campaignKey: null,
        loading: false,
        pageLoading: true,
        preRemovedCartItem: null,
        getCart: async () => {
          const campaignKey = await getActiveCampaignKey();
          const { response } = await getItems(campaignKey);

          updateCartState({
            cart: filterInStockProducts(response?.basket),
            loading: false,
            pageLoading: false,
            campaignKey,
          });
        },

        addToCart: async (referenceKey, q, pageType, limit = 10) => {
          updateCartState({ loading: true });

          if (!referenceKey) {
            showNotificationSizeNotSelected();
            return;
          }

          const limitedProductInCart = getState().cart?.items.find(
            (item) => item.variant.referenceKey === referenceKey,
          );

          if (
            limitedProductInCart &&
            limitedProductInCart.quantity + q > limit
          ) {
            showNotificationLimited({ count: limit });
            return;
          }

          let scayleProduct: BapiProduct | undefined;

          /** try to get variant */
          const resp = await getProductByKey(referenceKey, true);

          scayleProduct = resp.response;

          if (!scayleProduct) {
            /** try to get main product */
            const resp = await getProductByKey(referenceKey);
            scayleProduct = resp.response;
          }

          if (!scayleProduct?.variants?.length) {
            return;
          }

          let variantId;
          let variant;
          if (scayleProduct.referenceKey !== referenceKey) {
            variant = scayleProduct.variants.find(
              (variant) => variant.referenceKey === referenceKey,
            ) as Variant;

            variantId = variant.id;
          } else {
            variantId = scayleProduct.variants[0].id;
            variant = scayleProduct.variants[0];
          }

          /** need to send custom data to scayle  */
          const { label, category, masterCategory, brand } =
            Product.createProduct(scayleProduct);

          const { response } = await addItem({
            variantId: variantId,
            quantity: q,
            customData: {
              categoryName: masterCategory,
              brandName: brand,
              ...(category === GIFT_CARD_CATEGORY && { isGiftCard: true }),
              ...(label === DAILY_DEAL_PRODUCT_LABEL && { isDailyDeal: true }),
            },
            campaignKey: getState().campaignKey,
          });

          let basket = response?.basket;

          const productInCart = basket?.items.find(
            (item) => item.variant.id === variantId,
          );

          if (basket?.applicablePromotions?.length) {
            const promotions = basket?.applicablePromotions?.sort(
              (a, b) => a.promotion.priority - b.promotion.priority,
            );

            for (const promotion of promotions) {
              if (!!promotion.itemId) {
                const updatedResponse = await updateItem({
                  itemKey: promotion.itemId,
                  quantity: q,
                  promotionId: promotion.promotion.id,
                  campaignKey: getState().campaignKey,
                });
                basket = updatedResponse?.response?.basket;
              } else {
                const isApplicablePromotion =
                  promotion.promotion.conditions.every((condition) => {
                    return evaluateCondition(
                      condition.condition,
                      getState().cart,
                      scayleProduct,
                      variant,
                    );
                  });

                if (isApplicablePromotion && productInCart) {
                  const updatedResponse = await updateItem({
                    itemKey: productInCart?.key,
                    quantity: q,
                    promotionId: promotion.promotion.id,
                    campaignKey: getState().campaignKey,
                  });
                  basket = updatedResponse?.response?.basket;
                }
              }
            }
          }

          updateCartState({
            cart: filterInStockProducts(basket),
            preRemovedCartItem: null,
            loading: false,
          });

          const product =
            productInCart && Product.createProduct(productInCart.product);

          if (productInCart) {
            GTMCart.addProduct(
              productInCart as CartItem,
              getState().getCurrencyCode() || '',
              pageType,
              {
                quantity: q,
                price: productInCart.price.unit.withTax,
              },
            );
          }

          /** set order total price for order custom data for scayle */
          setOrderTotalPrice(response?.basket.cost.withTax || 0);

          showNotificationAddedToCart({
            name: product?.name,
            count: productInCart?.quantity,
          });
        },

        setSenderId: async (senderId) => {
          await setSenderId(senderId);
        },

        updateSender: scayleUpdateSender,

        updateInCart: async (id, q, pageType) => {
          const prevProduct = getState().getProductByKey(id);

          updateCartState({ loading: true });

          const { response } = await updateItem({
            itemKey: String(id),
            quantity: q,
            campaignKey: getState().campaignKey,
          });

          updateCartState({
            cart: filterInStockProducts(response?.basket),
            loading: false,
          });
          // GTM events
          if (response && prevProduct) {
            if (q > prevProduct.quantity) {
              GTMCart.incrementProduct(
                prevProduct,
                getState().getCurrencyCode(),
                q - prevProduct.quantity,
                pageType,
              );
            } else {
              GTMCart.decrementProduct(
                prevProduct,
                getState().getCurrencyCode(),
                prevProduct.quantity - q,
                pageType,
              );
            }
          }

          /** set order total price for order custom data for scayle */
          setOrderTotalPrice(response?.basket.cost.withTax || 0);
        },

        removeFromCart: async (key, name, pageType) => {
          updateCartState({ loading: true });
          const removedItem = getState().getProductByKey(key);

          const { response } = await removeItem(key, getState().campaignKey);

          updateCartState({
            cart: filterInStockProducts(response),
            preRemovedCartItem: removedItem,
            loading: false,
          });

          showNotificationPreRemove({
            name: name,
          });

          if (response && removedItem) {
            GTMCart.removeProduct(
              removedItem,
              getState().cart?.cost?.currencyCode,
              pageType,
            );
          }

          /** set order total price for order custom data for scayle */
          setOrderTotalPrice(response?.cost.withTax || 0);
        },

        clearPreRemovedCartItem: () => {
          updateCartState({
            preRemovedCartItem: null,
          });

          showNotificationRemove();
        },
        getProductByKey: (key) =>
          getState().cart?.items?.find((item) => item.key === key),
        getCurrencyCode: () => getState().cart?.cost?.currencyCode || '',
      }),
      {
        name: 'cart',
        storage: createJSONStorage(() => localStorage),
        /** @ts-ignore */
        partialize: ({ preRemovedCartItem: preremovedCartItem }) => ({
          preremovedCartItem,
        }),
      },
    ),
  ),
);
